import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { 
  Card, 
  CardHeader, 
  CardContent, 
  CardTitle, 
  CardDescription,
  Select,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from '../../../components/ui';
import { 
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  DocumentTextIcon,
  TrendingUpIcon
} from '../../../components/ui/Icons';
import { useApprovalAnalytics } from '../hooks/useApprovalRequests';
import { useFlexibleList } from '../../../hooks/useSWR';
import { useDashboardUi } from '../../../config/dashboardUiTheme';
import { useTableUiTheme } from '../../../contexts/TableUiThemeContext';
import { statusBadgeClassName } from '../../../config/tableBadgeStyles';
import dayjs from 'dayjs';

function approvalStatusSemantic(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('approv')) return 'success';
  if (s.includes('reject')) return 'danger';
  if (s.includes('cancel')) return 'secondary';
  if (s.includes('pend')) return 'warning';
  return 'neutral';
}

const AnalyticsPage = () => {
  const { theme } = useTheme();
  const dash = useDashboardUi(theme);
  const { themeId: tableUiThemeId } = useTableUiTheme();
  void tableUiThemeId;
  const [dateRange, setDateRange] = useState('30');
  const [entityType, setEntityType] = useState('');
  const [status, setStatus] = useState('');

  // Fetch analytics data using the proper hook
  const { analytics, isLoading: analyticsLoading, isError: analyticsError } = useApprovalAnalytics();

  // Fetch approval requests for detailed analysis
  const { data: approvalRequests, isLoading: requestsLoading } = useFlexibleList('/approval-requests');

  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };

  const formatPercentage = (value, total) => {
    if (!total || total === 0) return '0%';
    return ((value / total) * 100).toFixed(1) + '%';
  };

  const getStatusIcon = (status) => {
    const iconClass = cn('h-5 w-5 shrink-0', dash.iconText(approvalStatusSemantic(status)));
    switch (String(status || '').toLowerCase()) {
      case 'approved':
        return <CheckCircleIcon className={iconClass} />;
      case 'rejected':
        return <XCircleIcon className={iconClass} />;
      case 'cancelled':
        return <ExclamationTriangleIcon className={iconClass} />;
      default:
        return <ClockIcon className={iconClass} />;
    }
  };

  const getStatusBarFillClass = (status) => dash.fill(approvalStatusSemantic(status));

  // Calculate analytics from approval requests data if analytics endpoint is not available
  const calculateAnalytics = () => {
    if (!approvalRequests || approvalRequests.length === 0) {
      return {
        totalRequests: 0,
        approvedRequests: 0,
        pendingRequests: 0,
        rejectedRequests: 0,
        cancelledRequests: 0,
        overdueRequests: 0,
        averageProcessingTime: 0,
        statusDistribution: {},
        entityTypeDistribution: {}
      };
    }

    const filteredRequests = approvalRequests.filter(request => {
      const requestDate = new Date(request.requestedAt);
      const startDate = dayjs().subtract(parseInt(dateRange), 'days').toDate();
      const endDate = dayjs().toDate();
      
      // Handle entityType - can be string or object with name property
      const requestEntityType = request.entityType?.name || request.entityType;
      const matchesEntityType = !entityType || requestEntityType === entityType;
      
      // Handle status - can be string or object with name property
      const requestStatus = request.status?.name || request.status;
      const matchesStatus = !status || requestStatus === status;
      
      return requestDate >= startDate && requestDate <= endDate &&
             matchesEntityType &&
             matchesStatus;
    });

    const totalRequests = filteredRequests.length;
    const approvedRequests = filteredRequests.filter(r => {
      const rStatus = r.status?.name || r.status;
      return rStatus === 'approved';
    }).length;
    const pendingRequests = filteredRequests.filter(r => {
      const rStatus = r.status?.name || r.status;
      return rStatus === 'pending';
    }).length;
    const rejectedRequests = filteredRequests.filter(r => {
      const rStatus = r.status?.name || r.status;
      return rStatus === 'rejected';
    }).length;
    const cancelledRequests = filteredRequests.filter(r => {
      const rStatus = r.status?.name || r.status;
      return rStatus === 'cancelled';
    }).length;
    
    // Calculate overdue requests
    const overdueRequests = filteredRequests.filter(r => {
      const rStatus = r.status?.name || r.status;
      if (rStatus !== 'pending' || !r.dueDate) return false;
      return new Date(r.dueDate) < new Date();
    }).length;

    // Calculate average processing time
    const completedRequests = filteredRequests.filter(r => {
      const rStatus = r.status?.name || r.status;
      return rStatus === 'approved' || rStatus === 'rejected';
    });
    const totalProcessingTime = completedRequests.reduce((sum, r) => {
      if (r.approvedAt || r.rejectedAt) {
        const start = new Date(r.requestedAt);
        const end = new Date(r.approvedAt || r.rejectedAt);
        return sum + (end - start) / (1000 * 60 * 60 * 24); // Convert to days
      }
      return sum;
    }, 0);
    const averageProcessingTime = completedRequests.length > 0 
      ? totalProcessingTime / completedRequests.length 
      : 0;

    // Status distribution
    const statusDistribution = {
      approved: approvedRequests,
      pending: pendingRequests,
      rejected: rejectedRequests,
      cancelled: cancelledRequests
    };

    // Entity type distribution
    const entityTypeDistribution = {};
    filteredRequests.forEach(r => {
      const entityType = r.entityType?.name || r.entityType || 'unknown';
      entityTypeDistribution[entityType] = (entityTypeDistribution[entityType] || 0) + 1;
    });

    return {
      totalRequests,
      approvedRequests,
      pendingRequests,
      rejectedRequests,
      cancelledRequests,
      overdueRequests,
      averageProcessingTime,
      statusDistribution,
      entityTypeDistribution
    };
  };

  const analyticsData = analytics || calculateAnalytics();
  const isLoading = analyticsLoading || requestsLoading;
  const isError = analyticsError;

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center h-64', dash.pageBg())}>
        <div className="text-center">
          <div
            className={cn(
              'animate-spin rounded-full h-8 w-8 border-2 border-t-transparent mx-auto mb-4',
              theme.border.default,
              dash.fill('primary'),
            )}
          />
          <p className={cn('text-sm', theme.text.secondary)}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn('flex items-center justify-center h-64', dash.pageBg())}>
        <div className="text-center">
          <ExclamationTriangleIcon className={cn('h-12 w-12 mx-auto mb-4', dash.iconText('danger'))} />
          <p className={cn('text-sm', dash.text('danger'))}>Failed to load analytics data</p>
        </div>
      </div>
    );
  }

  const interactiveRow = cn(
    'rounded-lg border transition-colors hover:shadow-sm',
    theme.border.light,
    dash.listItem('hover:shadow-sm'),
  );

  const sectionTitleIcon = (Icon, semantic = 'info') => (
    <span className={cn('shrink-0', dash.iconText(semantic))}>
      <Icon className="w-5 h-5" />
    </span>
  );

  return (
    <div className={cn('min-h-full w-full max-w-none space-y-6 px-3 py-4 md:px-4 md:py-5', dash.pageBg())}>
      {/* Header */}
      <header className={cn('relative w-full overflow-hidden rounded-2xl border', dash.widgetShell())}>
        <div className={cn('h-1.5 w-full', theme.gradients.header)} />
        <div className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className={cn("text-3xl font-bold mb-2", theme.text.primary)}>Approval Analytics</h1>
            <p className={cn("text-sm", theme.text.secondary)}>
              Comprehensive insights into approval workflows and performance metrics
            </p>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-3 w-full md:w-auto">
            <Select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </Select>
            
            <Select value={entityType} onChange={(e) => setEntityType(e.target.value)}>
              <SelectItem value="">All Entity Types</SelectItem>
              <SelectItem value="document">Document</SelectItem>
              <SelectItem value="capa">CAPA</SelectItem>
              <SelectItem value="change_request">Change Request</SelectItem>
              <SelectItem value="non_conformity">Non-Conformity</SelectItem>
              <SelectItem value="non_conformity_report">Non-Conformity Report</SelectItem>
              <SelectItem value="complaint">Complaint</SelectItem>
              <SelectItem value="risk_assessment">Risk Assessment</SelectItem>
              <SelectItem value="deviation">Deviation</SelectItem>
              <SelectItem value="training">Training</SelectItem>
              <SelectItem value="supplier">Supplier</SelectItem>
            </Select>
            
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <SelectItem value="">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </Select>
          </div>
        </div>
        </div>
      </header>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            label: 'Total Requests',
            value: formatNumber(analyticsData?.totalRequests || 0),
            sub: 'All time requests',
            Icon: DocumentTextIcon,
            semantic: 'info',
          },
          {
            label: 'Approval Rate',
            value: formatPercentage(analyticsData?.approvedRequests || 0, analyticsData?.totalRequests || 0),
            sub: `${formatNumber(analyticsData?.approvedRequests || 0)} approved`,
            Icon: TrendingUpIcon,
            semantic: 'success',
          },
          {
            label: 'Avg. Processing Time',
            value: analyticsData?.averageProcessingTime?.toFixed(1) || 0,
            sub: 'days average',
            Icon: ClockIcon,
            semantic: 'warning',
          },
          {
            label: 'Overdue Requests',
            value: formatNumber(analyticsData?.overdueRequests || 0),
            sub: 'Requires attention',
            Icon: ExclamationTriangleIcon,
            semantic: 'danger',
          },
        ].map((metric) => (
          <div key={metric.label} className={cn(dash.metricCard(), 'p-6 group')}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className={cn('text-sm font-medium mb-1', theme.text.secondary)}>{metric.label}</p>
                <p className={cn('text-3xl font-bold mb-1', theme.text.primary)}>{metric.value}</p>
                <p className={cn('text-xs', theme.text.muted)}>{metric.sub}</p>
              </div>
              <div className={cn(dash.iconText(metric.semantic), 'shrink-0')}>
                <metric.Icon className="w-8 h-8" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Analytics */}
      <section className={cn('relative w-full overflow-hidden rounded-2xl', dash.widgetShell('p-4 md:p-6'))}>
        <Tabs defaultValue="overview" className="w-full">
          <TabsList
            className={cn(
              'grid w-full grid-cols-2 sm:grid-cols-4 gap-1 rounded-xl p-1 border',
              theme.border.light,
              dash.isRefined ? 'bg-slate-100/80 dark:bg-slate-800/50' : theme.colors.background,
            )}
          >
            {[
              { value: 'overview', label: 'Overview' },
              { value: 'status', label: 'Status Breakdown' },
              { value: 'entities', label: 'Entity Types' },
              { value: 'performance', label: 'Performance' },
            ].map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className={cn(
                  'rounded-lg text-xs sm:text-sm transition-colors',
                  theme.text.secondary,
                  dash.isRefined
                    ? 'data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:shadow-sm data-[state=active]:text-slate-900 dark:data-[state=active]:text-slate-100'
                    : cn(
                        'data-[state=active]:shadow-sm data-[state=active]:text-inherit',
                        `data-[state=active]:${theme.colors.surface}`,
                      ),
                )}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Status Distribution */}
          <Card className={dash.widgetShell('shadow-sm p-0')}>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-xl">
                {sectionTitleIcon(ChartBarIcon, 'info')}
                Status Distribution
              </CardTitle>
              <CardDescription className="mt-2">
                Breakdown of approval requests by status
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(() => {
                  // Use API data if available, otherwise use calculated data
                  const statusData = analytics?.statusBreakdown || 
                    Object.entries(analyticsData?.statusDistribution || {}).map(([status, count]) => ({
                      status: status,
                      count: count
                    }));
                  
                  if (!statusData || statusData.length === 0 || analyticsData?.totalRequests === 0) {
                    return (
                      <div className="text-center py-12">
                        <div
                          className={cn(
                            'w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center',
                            theme.colors.background,
                          )}
                        >
                          <ChartBarIcon className={cn('w-8 h-8', theme.text.muted)} />
                        </div>
                        <p className={cn("text-sm font-medium mb-1", theme.text.primary)}>No status data available</p>
                        <p className={cn("text-xs", theme.text.secondary)}>
                          Approval requests will appear here once data is available
                        </p>
                      </div>
                    );
                  }
                  
                  return statusData.map((item) => {
                    const status = item.status?.name || item.status;
                    const count = item.count || 0;
                    const percentage = (count / (analyticsData?.totalRequests || 1)) * 100;
                    return (
                      <div key={status} className={cn('group p-4', interactiveRow)}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className={cn('p-2 rounded-lg', theme.colors.background)}>
                              {getStatusIcon(status)}
                            </div>
                            <span className={cn("font-semibold text-base", theme.text.primary)}>
                              {status?.charAt(0).toUpperCase() + status?.slice(1)}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className={cn("text-lg font-bold", theme.text.primary)}>
                              {formatNumber(count)}
                            </span>
                            <span className={cn("text-sm ml-2", theme.text.secondary)}>
                              ({formatPercentage(count, analyticsData?.totalRequests || 0)})
                            </span>
                          </div>
                        </div>
                        <div className={cn(dash.progressTrack, 'h-3 overflow-hidden')}>
                          <div
                            className={cn(getStatusBarFillClass(status), 'h-3 rounded-full transition-all duration-500')}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className={dash.widgetShell('shadow-sm p-0')}>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-xl">
                {sectionTitleIcon(CalendarIcon, 'secondary')}
                Recent Activity
              </CardTitle>
              <CardDescription className="mt-2">
                Latest approval requests and their status
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!approvalRequests || approvalRequests.length === 0 ? (
                <div className="text-center py-12">
                  <div
                    className={cn(
                      'w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center',
                      theme.colors.background,
                    )}
                  >
                    <CalendarIcon className={cn('w-8 h-8', theme.text.muted)} />
                  </div>
                  <p className={cn("text-sm font-medium mb-1", theme.text.primary)}>No recent activity</p>
                  <p className={cn("text-xs", theme.text.secondary)}>
                    Approval requests will appear here as they are created
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {approvalRequests.slice(0, 10).map((request) => {
                    const requestStatus = request.status?.name || request.status;
                    return (
                      <div 
                        key={request.id} 
                        className={cn('flex items-center justify-between p-4', interactiveRow, 'group')}
                      >
                        <div className="flex items-center space-x-4 flex-1 min-w-0">
                          <div className={cn('flex-shrink-0 p-2 rounded-lg', theme.colors.background)}>
                            {getStatusIcon(requestStatus)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn("font-semibold truncate", theme.text.primary)}>{request.title}</p>
                            <div className="flex items-center space-x-2 mt-1">
                              <p className={cn("text-sm", theme.text.secondary)}>
                                {request.entityType?.name || request.entityType || 'Unknown'}
                              </p>
                              <span className={cn('opacity-60', theme.text.muted)}>•</span>
                              <p className={cn("text-sm", theme.text.secondary)}>
                                {dayjs(request.requestedAt).format('MMM DD, YYYY')}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-4">
                          <span className={statusBadgeClassName(requestStatus, theme)}>
                            {(requestStatus)?.charAt(0).toUpperCase() + (requestStatus)?.slice(1)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="status" className="space-y-6 mt-6">
          {/* Status Trends */}
          <Card className={dash.widgetShell('shadow-sm p-0')}>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-xl">
                {sectionTitleIcon(ChartBarIcon, 'primary')}
                Status Trends
              </CardTitle>
              <CardDescription className="mt-2">
                How approval statuses have changed over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <div
                  className={cn(
                    'w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center',
                    theme.colors.background,
                  )}
                >
                  <ChartBarIcon className={cn('w-8 h-8', theme.text.muted)} />
                </div>
                <p className={cn("text-sm font-medium mb-1", theme.text.primary)}>Status trends chart</p>
                <p className={cn("text-xs", theme.text.secondary)}>
                  This feature will be implemented in a future update
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="entities" className="space-y-6 mt-6">
          {/* Entity Type Distribution */}
          <Card className={dash.widgetShell('shadow-sm p-0')}>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-xl">
                {sectionTitleIcon(ChartBarIcon, 'success')}
                Entity Type Distribution
              </CardTitle>
              <CardDescription className="mt-2">
                Breakdown of approval requests by entity type
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(() => {
                  // Use API data if available, otherwise use calculated data
                  const entityData = analytics?.entityTypeBreakdown || 
                    Object.entries(analyticsData?.entityTypeDistribution || {}).map(([entityType, count]) => ({
                      entityType: entityType,
                      count: count
                    }));
                  
                  if (!entityData || entityData.length === 0 || analyticsData?.totalRequests === 0) {
                    return (
                      <div className="text-center py-12">
                        <div
                          className={cn(
                            'w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center',
                            theme.colors.background,
                          )}
                        >
                          <ChartBarIcon className={cn('w-8 h-8', theme.text.muted)} />
                        </div>
                        <p className={cn("text-sm font-medium mb-1", theme.text.primary)}>No entity type data available</p>
                        <p className={cn("text-xs", theme.text.secondary)}>
                          Entity type distribution will appear here once data is available
                        </p>
                      </div>
                    );
                  }
                  
                  const entityBarSemantics = ['info', 'primary', 'success', 'warning', 'danger', 'secondary'];
                  return entityData.map((item, entityIndex) => {
                    const entityType = item.entityType?.name || item.entityType;
                    const count = item.count || 0;
                    const percentage = (count / (analyticsData?.totalRequests || 1)) * 100;
                    const displayName = entityType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    const barSemantic = entityBarSemantics[entityIndex % entityBarSemantics.length];
                    return (
                      <div key={entityType} className={cn('group p-4', interactiveRow)}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className={cn('w-3 h-3 rounded-full shrink-0', dash.dot(barSemantic))} />
                            <span className={cn("font-semibold text-base", theme.text.primary)}>
                              {displayName}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className={cn("text-lg font-bold", theme.text.primary)}>
                              {formatNumber(count)}
                            </span>
                            <span className={cn("text-sm ml-2", theme.text.secondary)}>
                              ({formatPercentage(count, analyticsData?.totalRequests || 0)})
                            </span>
                          </div>
                        </div>
                        <div className={cn(dash.progressTrack, 'h-3 overflow-hidden')}>
                          <div
                            className={cn(dash.fill(barSemantic), 'h-3 rounded-full transition-all duration-500')}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-6 mt-6">
          {/* Performance Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className={dash.widgetShell('shadow-sm p-0')}>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  {sectionTitleIcon(ClockIcon, 'warning')}
                  Processing Time Analysis
                </CardTitle>
                <CardDescription className="mt-2">
                  Average processing times by status
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!analytics?.processingTimeByStatus || analytics.processingTimeByStatus.length === 0 ? (
                  <div className="text-center py-8">
                    <p className={cn("text-sm", theme.text.secondary)}>No processing time data available</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {analytics.processingTimeByStatus.map((item) => (
                      <div
                        key={item.status?.id || item.status}
                        className={cn('flex items-center justify-between p-3', dash.listItem())}
                      >
                        <span className={cn("text-sm font-medium", theme.text.primary)}>
                          {(item.status?.name || item.status)?.charAt(0).toUpperCase() + (item.status?.name || item.status)?.slice(1)}
                        </span>
                        <span className={cn("font-bold text-base", theme.text.primary)}>
                          {item.averageDays} days
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className={dash.widgetShell('shadow-sm p-0')}>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  {sectionTitleIcon(TrendingUpIcon, 'success')}
                  Approver Performance
                </CardTitle>
                <CardDescription className="mt-2">
                  Top performing approvers
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!analytics?.topApprovers || analytics.topApprovers.length === 0 ? (
                  <div className="text-center py-8">
                    <p className={cn("text-sm", theme.text.secondary)}>No approver performance data available</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {analytics.topApprovers.map((approver, index) => (
                      <div
                        key={approver.id}
                        className={cn('flex items-center justify-between p-3', dash.listItem())}
                      >
                        <div className="flex items-center space-x-3">
                          <span
                            className={cn(
                              'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0',
                              dash.badge('info'),
                            )}
                          >
                            {index + 1}
                          </span>
                          <span className={cn("text-sm font-medium", theme.text.primary)}>
                            {approver.name || approver.userDisplayName}
                          </span>
                        </div>
                        <span className={cn("text-sm font-bold", theme.text.primary)}>
                          {approver.approvalCount} approvals
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      </section>
    </div>
  );
};

export default AnalyticsPage;
